import { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FilterMatchMode } from 'primereact/api';
import {
    useAddLmsTrainingDocumentMutation,
    useDeleteLmsTrainingDocumentMutation,
    useGetLmsTrainingDocumentsQuery,
    useGetLmsVehicleTrainingMatchesQuery,
    useLazyDownloadByKeyQuery,
    useUpdateLmsTrainingDocumentMutation
} from '../../../../api';
import { useLanguage } from '../../../../contexts/LanguageContext';
import toastService from '../../../../utils/toastService';
import { getTrainingDocumentFileName } from '../utils/documentFileName';

const PDF_MIME_TYPE = 'application/pdf';
const isPdf = (file) => !file || file.type === PDF_MIME_TYPE || file.name?.toLowerCase().endsWith('.pdf');
const initialFilters = {
    vehicleName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    trainingName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    description: { value: null, matchMode: FilterMatchMode.CONTAINS },
    visibility: { value: null, matchMode: FilterMatchMode.EQUALS }
};

const getGroupVisibility = (documents) => {
    if (documents.every((document) => document.isPublic)) return 'public';
    if (documents.every((document) => !document.isPublic)) return 'private';
    return 'mixed';
};

const useTrainingDocumentData = () => {
    const { th } = useLanguage();
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [filters, setFilters] = useState(initialFilters);
    const [addDialogVisible, setAddDialogVisible] = useState(false);
    const [previewDocument, setPreviewDocument] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [addDocument] = useAddLmsTrainingDocumentMutation();
    const [updateDocument] = useUpdateLmsTrainingDocumentMutation();
    const [deleteDocument, { isLoading: isDeleting }] = useDeleteLmsTrainingDocumentMutation();
    const [downloadByKey, { isFetching: isFetchingPreview }] = useLazyDownloadByKeyQuery();

    useEffect(
        () => () => {
            if (previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
        },
        [previewUrl]
    );

    const formik = useFormik({
        initialValues: { vehicleCode: '', vehicleTrainingMatchId: '', description: '', isPublic: false, files: [] },
        validationSchema: Yup.object({
            vehicleCode: Yup.string().required(th('lmsTrainingDocument.validation.vehicleRequired')),
            vehicleTrainingMatchId: Yup.number().typeError(th('lmsTrainingDocument.validation.trainingRequired')).required(th('lmsTrainingDocument.validation.trainingRequired')),
            description: Yup.string().trim().required(th('lmsTrainingDocument.validation.descriptionRequired')),
            files: Yup.array()
                .min(1, th('lmsTrainingDocument.validation.fileRequired'))
                .required(th('lmsTrainingDocument.validation.fileRequired'))
                .test('pdf-files', th('lmsTrainingDocument.validation.pdfOnly'), (files) => !files || files.every(isPdf))
        }),
        onSubmit: async ({ vehicleTrainingMatchId, description, isPublic, files }, helpers) => {
            try {
                for (const file of files) {
                    await addDocument({ vehicleTrainingMatchId: Number(vehicleTrainingMatchId), description: description.trim(), file, isPublic }).unwrap();
                }
                toastService.success(th('lmsTrainingDocument.saveSuccess'), th('common.success'));
                resetAddForm(helpers);
                setAddDialogVisible(false);
            } catch {
                return;
            }
        }
    });

    const editFormik = useFormik({
        initialValues: { description: '', isPublic: false, files: [], removedDocumentIds: [] },
        validationSchema: Yup.object({
            description: Yup.string().trim().required(th('lmsTrainingDocument.validation.descriptionRequired')),
            files: Yup.array()
                .test('pdf-files', th('lmsTrainingDocument.validation.pdfOnly'), (files) => !files || files.every(isPdf))
                .test('document-required', th('lmsTrainingDocument.validation.fileRequired'), function (files) {
                    const remainingDocumentCount = selectedDocument?.documents.filter(({ documentId }) => !this.parent.removedDocumentIds.includes(documentId)).length ?? 0;
                    return Boolean(files?.length) || remainingDocumentCount > 0;
                })
        }),
        onSubmit: async ({ description, isPublic, files, removedDocumentIds }) => {
            if (!selectedDocument?.vehicleTrainingMatchId) return;
            try {
                const trimmedDescription = description.trim();
                for (const document of selectedDocument.documents) {
                    if (!removedDocumentIds.includes(document.documentId) && (trimmedDescription !== document.description.trim() || isPublic !== Boolean(document.isPublic))) {
                        await updateDocument({ documentId: document.documentId, description: trimmedDescription, isPublic }).unwrap();
                    }
                }
                for (const documentId of removedDocumentIds) {
                    await deleteDocument({ documentId }).unwrap();
                }
                for (const file of files) {
                    await addDocument({ vehicleTrainingMatchId: Number(selectedDocument.vehicleTrainingMatchId), description: trimmedDescription, file, isPublic }).unwrap();
                }
                toastService.success(th('lmsTrainingDocument.updateSuccess'), th('common.success'));
                closeEditDialog();
            } catch {
                return;
            }
        }
    });

    const resetAddForm = (form = formik) => {
        form.setFieldValue('description', '', false);
        form.setFieldValue('isPublic', false, false);
        form.setFieldValue('files', [], false);
        form.setTouched({});
    };

    const { data: allMatches = [], isFetching: isFetchingVehicles } = useGetLmsVehicleTrainingMatchesQuery({});
    const { data: trainingMatches = [], isFetching: isFetchingTrainings } = useGetLmsVehicleTrainingMatchesQuery({ vehicleCode: formik.values.vehicleCode }, { skip: !formik.values.vehicleCode });
    const vehicleTrainingMatchIds = useMemo(() => allMatches.map((match) => Number(match.id)).filter((id) => Number.isInteger(id) && id > 0), [allMatches]);
    const { data: documents = [], isFetching: isFetchingDocumentList } = useGetLmsTrainingDocumentsQuery(vehicleTrainingMatchIds, { skip: vehicleTrainingMatchIds.length === 0 });

    const vehicleOptions = useMemo(() => {
        const vehicles = new Map();
        allMatches.forEach((match) => {
            if (match.vehicleCode && !vehicles.has(match.vehicleCode)) vehicles.set(match.vehicleCode, { label: match.vehicleName, value: match.vehicleCode });
        });
        return Array.from(vehicles.values());
    }, [allMatches]);

    const trainingOptions = useMemo(() => trainingMatches.filter((match) => match.id).map((match) => ({ label: match.trainingName, value: match.id })), [trainingMatches]);

    const documentItems = useMemo(() => {
        const matchesById = new Map(allMatches.map((match) => [Number(match.id), match]));
        const groupedDocuments = new Map();
        documents.forEach((document) => {
            const match = matchesById.get(Number(document.vehicleTrainingMatchId));
            const groupKey = Number(document.vehicleTrainingMatchId);
            const existingGroup = groupedDocuments.get(groupKey);
            if (existingGroup) {
                existingGroup.documents.push(document);
                return;
            }
            groupedDocuments.set(groupKey, {
                ...document,
                vehicleName: match?.vehicleName,
                trainingName: match?.trainingName,
                documents: [document]
            });
        });
        return Array.from(groupedDocuments.values()).map((group) => ({ ...group, visibility: getGroupVisibility(group.documents) }));
    }, [allMatches, documents]);

    const selectVehicle = (vehicleCode) => {
        formik.setFieldValue('vehicleCode', vehicleCode);
        formik.setFieldValue('vehicleTrainingMatchId', '');
    };

    const selectFiles = (files) => {
        formik.setFieldTouched('files', true, false);
        formik.setFieldValue('files', files ?? []);
    };

    const openEditDialog = (document) => {
        setSelectedDocument(document);
        editFormik.setValues({ description: document.description ?? '', isPublic: document.visibility === 'public', files: [], removedDocumentIds: [] });
    };

    const closeEditDialog = () => {
        editFormik.resetForm();
        setSelectedDocument(null);
    };

    const fetchDocumentBlob = async (document) => {
        return downloadByKey(document.fileKey).unwrap();
    };

    const openPreviewDialog = async (document) => {
        setPreviewDocument(document);
        setPreviewUrl('');

        try {
            const blob = await fetchDocumentBlob(document);
            setPreviewUrl(URL.createObjectURL(blob.type ? blob : new Blob([blob], { type: PDF_MIME_TYPE })));
        } catch {
            setPreviewDocument(null);
        }
    };

    const downloadTrainingDocument = async (document) => {
        try {
            const blob = await fetchDocumentBlob(document);
            const url = URL.createObjectURL(blob);
            const link = window.document.createElement('a');
            link.href = url;
            link.download = getTrainingDocumentFileName(document);
            window.document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch {
            return;
        }
    };

    const closePreviewDialog = () => {
        setPreviewDocument(null);
        setPreviewUrl('');
    };

    const removeDocument = async (document) => {
        if (isDeleting || document.documents.length === 0) return;
        try {
            for (const { documentId } of document.documents) {
                await deleteDocument({ documentId }).unwrap();
            }
            toastService.success(th('lmsTrainingDocument.deleteSuccess'), th('common.success'));
            closeEditDialog();
        } catch {
            return;
        }
    };

    return {
        formik,
        editFormik,
        vehicleOptions,
        trainingOptions,
        documents: documentItems,
        filters,
        selectedDocument,
        addDialogVisible,
        previewDocument,
        previewUrl,
        isFetchingVehicles,
        isFetchingTrainings,
        isFetchingDocuments: isFetchingVehicles || isFetchingDocumentList,
        isDeleting,
        isFetchingPreview,
        selectVehicle,
        selectFiles,
        onFilter: (event) => setFilters(event.filters),
        openAddDialog: () => setAddDialogVisible(true),
        closeAddDialog: () => {
            resetAddForm();
            setAddDialogVisible(false);
        },
        openEditDialog,
        closeEditDialog,
        openPreviewDialog,
        closePreviewDialog,
        downloadTrainingDocument,
        removeDocument
    };
};

export default useTrainingDocumentData;
